// RRPoolBuilder — full manual pool control for admins. RR Update 1 (Feb 2026).
//
// Feb 2026 UI redesign: pools stack VERTICALLY as full-width sections,
// each with horizontal team rows. This replaces the earlier 3-column
// layout that squeezed player names into narrow tiles. Business logic is
// UNCHANGED — same API calls, same state shape, same enable/disable rules.
//
// Behavior (unchanged):
// - Move an entry between pools via the per-entry Pool dropdown.
// - Reorder inside a pool with up/down buttons; commits `seed` via
//   `POST /pools/{pn}/seed` with the entire pool_number's entry_ids in
//   the desired order.
// - Rename / delete / create / reorder pools via the pool header controls.
// - Inline team-name editing (Enter commits, Esc cancels).
// - Disabled entirely once the RR is `schedule_generated`, `in_progress`,
//   `completed`, `archived`, or `cancelled`. Backend enforces the same.

import React, { useMemo, useState, useEffect } from 'react';
import {
  ArrowUp, ArrowDown, Pencil, Trash, Plus, ArrowsClockwise, Check, X, CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { roundRobinAPI } from '../../lib/roundRobinApi';
import EntryDisplay from '../EntryDisplay';

const POOL_LOCKED_STATUSES = new Set([
  'schedule_generated', 'in_progress', 'completed', 'archived', 'cancelled',
]);

function poolLabelFor(rr, poolNumber) {
  const labels = rr.pool_labels || {};
  return labels[String(poolNumber)] || `Pool ${poolNumber}`;
}

function reportError(err, fallback) {
  const d = err?.response?.data?.detail;
  const msg = typeof d === 'string' ? d : (d?.message || fallback);
  toast.error(msg);
}

// Sort pool entries by their current seed (nulls last), then created_at,
// then id — same ordering the backend uses at schedule time.
function sortPoolEntries(entries) {
  return [...entries].sort((a, b) => {
    const sa = a.seed == null ? Number.POSITIVE_INFINITY : a.seed;
    const sb = b.seed == null ? Number.POSITIVE_INFINITY : b.seed;
    if (sa !== sb) return sa - sb;
    const ca = a.created_at || '';
    const cb = b.created_at || '';
    if (ca !== cb) return ca < cb ? -1 : 1;
    return (a.id || '') < (b.id || '') ? -1 : 1;
  });
}

// Small subtle inline editor. When idle it renders as a pencil-only
// affordance so it never competes with the player names.
function TeamNameField({ entry, disabled, rrId, onChanged }) {
  const [value, setValue] = useState(entry.team_name || '');
  const [editing, setEditing] = useState(false);

  const commit = async () => {
    const next = value.trim();
    if ((entry.team_name || '') === next) { setEditing(false); return; }
    try {
      await roundRobinAPI.updateEntry(rrId, entry.id, { team_name: next || null });
      toast.success('Team name saved.');
      setEditing(false);
      onChanged?.();
    } catch (e) { reportError(e, 'Failed to save team name.'); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') { setEditing(false); setValue(entry.team_name || ''); }
          }}
          placeholder="Team name"
          maxLength={80}
          data-testid={`rrpb-team-name-input-${entry.id}`}
          className="text-xs px-2 py-1 border border-[#e3dcd1] rounded-md w-44"
        />
        <button
          onClick={commit}
          data-testid={`rrpb-team-name-save-${entry.id}`}
          className="text-emerald-700"
          type="button"
        >
          <Check size={14} weight="bold" />
        </button>
        <button
          onClick={() => { setEditing(false); setValue(entry.team_name || ''); }}
          className="text-[#9aa39d]"
          type="button"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Idle state: pencil icon only. Tooltip surfaces whether a name exists.
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      data-testid={`rrpb-team-name-edit-${entry.id}`}
      title={entry.team_name ? `Edit team name (“${entry.team_name}”)` : 'Add team name'}
      className={`inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#ece6dc] text-[#6a766e] hover:text-[#013523] hover:border-[#013523] disabled:opacity-40 disabled:cursor-not-allowed ${
        entry.team_name ? 'bg-[#f5f0e6]' : 'bg-white'
      }`}
    >
      <Pencil size={13} />
    </button>
  );
}

// Court Priority for one pool (auto court-assignment quality only). Lower
// number = higher priority. Empty = unset (lowest). Positive integers only;
// commits on blur / Enter. Editable even after the schedule is generated —
// it only influences the NEXT auto-schedule run, never already-placed courts.
function CourtPriorityField({ poolNumber, value, editable, onCommit }) {
  const [v, setV] = useState(value == null || value === '' ? '' : String(value));
  useEffect(() => {
    setV(value == null || value === '' ? '' : String(value));
  }, [value]);

  const commit = () => {
    const t = v.trim();
    if (t === '') { onCommit(null); return; }
    const n = Number(t);
    if (!Number.isInteger(n) || n < 1) {
      toast.error('Court Priority must be a positive integer (1 = highest).');
      setV(value == null || value === '' ? '' : String(value));
      return;
    }
    onCommit(n);
  };

  return (
    <div className="flex items-center gap-1.5 shrink-0" title="Court Priority — lower = better courts first (1 = highest). Leave blank for lowest.">
      <span className="text-[10px] uppercase tracking-wider text-[#9aa39d] font-medium">Court Priority</span>
      <input
        type="number"
        min={1}
        step={1}
        value={v}
        disabled={!editable}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        placeholder="—"
        data-testid={`rrpb-pool-priority-${poolNumber}`}
        className="w-14 px-2 py-1 border border-[#e3dcd1] rounded-md text-xs text-center tabular-nums disabled:opacity-50 disabled:cursor-not-allowed"
      />
      <span className="text-[10px] text-[#9aa39d]">{value === 1 ? 'Highest' : ''}</span>
    </div>
  );
}

// One team row inside a pool (or the Unassigned section).
//   grid: [seed] [team name/players] [+team-name] [pool select] [up/down]
// All rows in a pool share this exact template so seeds, names, and
// controls line up vertically across the whole section.
function TeamRow({
  entry, rrId, disabled, poolChoices, currentPool, seedNumber,
  onChanged, onMoveUp, onMoveDown, isFirst, isLast,
}) {
  const changePool = async (e) => {
    const raw = e.target.value;
    const target = raw === '' ? null : parseInt(raw, 10);
    if (target === currentPool) return;
    try {
      await roundRobinAPI.assignPools(rrId, [{ entry_id: entry.id, pool_number: target }]);
      onChanged?.();
    } catch (err) { reportError(err, 'Move failed.'); }
  };

  return (
    <div
      data-testid={`rrpb-entry-${entry.id}`}
      className="grid items-center gap-3 px-3 sm:px-4 py-2.5 border border-[#ece6dc] rounded-xl bg-white hover:border-[#d6cfc2] transition-colors"
      style={{ gridTemplateColumns: '36px minmax(0, 1fr) auto auto auto' }}
    >
      {/* Seed chip — small, dark green, never dominates */}
      <div
        data-testid={`rrpb-seed-${entry.id}`}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#013523] text-white text-sm font-semibold tabular-nums"
        aria-label={currentPool != null ? `Seed ${seedNumber}` : 'Unseeded'}
      >
        {currentPool != null ? seedNumber : '—'}
      </div>

      {/* Team / player names — the star of the row */}
      <div className="min-w-0 text-[#013523] text-sm sm:text-[15px] leading-snug">
        <EntryDisplay entry={entry} />
      </div>

      {/* Team-name edit affordance */}
      <TeamNameField
        entry={entry}
        disabled={disabled}
        rrId={rrId}
        onChanged={onChanged}
      />

      {/* Pool selector */}
      <select
        value={currentPool ?? ''}
        onChange={changePool}
        disabled={disabled}
        data-testid={`rrpb-entry-move-${entry.id}`}
        className="text-xs px-2 py-1.5 border border-[#e3dcd1] rounded-md bg-white text-[#013523] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">— Unassigned</option>
        {poolChoices.map((c) => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>

      {/* Up / Down reorder — only meaningful when the team is assigned */}
      {currentPool != null ? (
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={disabled || isFirst}
            onClick={onMoveUp}
            data-testid={`rrpb-entry-up-${entry.id}`}
            title="Move up (better seed)"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#ece6dc] text-[#6a766e] hover:text-[#013523] hover:border-[#013523] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowUp size={13} weight="bold" />
          </button>
          <button
            type="button"
            disabled={disabled || isLast}
            onClick={onMoveDown}
            data-testid={`rrpb-entry-down-${entry.id}`}
            title="Move down (lower seed)"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-[#ece6dc] text-[#6a766e] hover:text-[#013523] hover:border-[#013523] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowDown size={13} weight="bold" />
          </button>
        </div>
      ) : (
        // Placeholder keeps grid columns aligned when in Unassigned section
        <div className="w-[62px]" aria-hidden />
      )}
    </div>
  );
}

// One full-width pool section. Header on top, team rows stacked beneath.
function PoolSection({
  rr, poolNumber, entries, disabled, onChanged, poolChoices,
  canDelete, canMoveUp, canMoveDown, onReorderPool,
  priority, priorityEditable, onPriorityChange,
}) {
  const [renaming, setRenaming] = useState(false);
  const label = poolLabelFor(rr, poolNumber);
  const [newLabel, setNewLabel] = useState(label);
  const sorted = useMemo(() => sortPoolEntries(entries), [entries]);

  const commitRename = async () => {
    try {
      const next = newLabel.trim();
      await roundRobinAPI.renamePool(rr.id, poolNumber, next || null);
      setRenaming(false);
      toast.success('Pool renamed.');
      onChanged?.();
    } catch (e) { reportError(e, 'Rename failed.'); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete ${label}? Higher-numbered pools shift down by 1.`)) return;
    try {
      await roundRobinAPI.deletePool(rr.id, poolNumber);
      toast.success('Pool deleted.');
      onChanged?.();
    } catch (e) { reportError(e, 'Delete failed.'); }
  };

  const moveUp = async (idx) => {
    const reordered = [...sorted];
    [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
    try {
      await roundRobinAPI.seedPool(rr.id, poolNumber, reordered.map((e) => e.id));
      onChanged?.();
    } catch (e) { reportError(e, 'Reorder failed.'); }
  };
  const moveDown = async (idx) => moveUp(idx + 1);

  return (
    <section
      className="border border-[#e3dcd1] rounded-2xl bg-white overflow-hidden"
      data-testid={`rrpb-pool-${poolNumber}`}
    >
      {/* Header row — pool identity on the left, controls clustered right */}
      <header className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-[#fbfaf8] border-b border-[#f0ebe1] flex-wrap">
        {renaming ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              autoFocus
              type="text"
              value={newLabel}
              maxLength={64}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setRenaming(false); setNewLabel(label); }
              }}
              data-testid={`rrpb-pool-rename-input-${poolNumber}`}
              className="text-sm px-2 py-1.5 border border-[#e3dcd1] rounded-md w-56"
            />
            <button
              onClick={commitRename}
              data-testid={`rrpb-pool-rename-save-${poolNumber}`}
              className="text-emerald-700"
              type="button"
            >
              <Check size={16} weight="bold" />
            </button>
            <button
              onClick={() => { setRenaming(false); setNewLabel(label); }}
              className="text-[#9aa39d]"
              type="button"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <span
                className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[#013523] text-white text-[11px] font-semibold shrink-0 tabular-nums"
                data-testid={`rrpb-pool-index-${poolNumber}`}
              >
                #{poolNumber}
              </span>
              <h3
                className="text-base sm:text-lg font-semibold text-[#013523] truncate"
                data-testid={`rrpb-pool-label-${poolNumber}`}
              >
                {label}
              </h3>
              <span
                className="text-[11px] uppercase tracking-wider text-[#9aa39d] font-medium"
                data-testid={`rrpb-pool-count-${poolNumber}`}
              >
                {sorted.length} {sorted.length === 1 ? 'team' : 'teams'}
              </span>
            </div>
            <CourtPriorityField
              poolNumber={poolNumber}
              value={priority}
              editable={priorityEditable}
              onCommit={onPriorityChange}
            />
            {!disabled && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  disabled={!canMoveUp}
                  onClick={() => onReorderPool(-1)}
                  data-testid={`rrpb-pool-left-${poolNumber}`}
                  title="Move pool up"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#ece6dc] text-[#6a766e] hover:text-[#013523] hover:border-[#013523] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowUp size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  disabled={!canMoveDown}
                  onClick={() => onReorderPool(1)}
                  data-testid={`rrpb-pool-right-${poolNumber}`}
                  title="Move pool down"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#ece6dc] text-[#6a766e] hover:text-[#013523] hover:border-[#013523] disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowDown size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => setRenaming(true)}
                  data-testid={`rrpb-pool-rename-${poolNumber}`}
                  title="Rename pool"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#ece6dc] text-[#6a766e] hover:text-[#013523] hover:border-[#013523]"
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  disabled={!canDelete}
                  onClick={doDelete}
                  data-testid={`rrpb-pool-delete-${poolNumber}`}
                  title={canDelete ? 'Delete pool' : 'Delete only allowed when pool is empty and more than one pool exists'}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#ece6dc] text-red-700 hover:border-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Trash size={13} />
                </button>
              </div>
            )}
          </>
        )}
      </header>

      {/* Body — team rows */}
      <div className="p-3 sm:p-4">
        {sorted.length === 0 ? (
          <div
            className="text-xs text-[#9aa39d] italic text-center py-6 border border-dashed border-[#ece6dc] rounded-xl"
            data-testid={`rrpb-pool-empty-${poolNumber}`}
          >
            Empty pool. Move teams here from another pool or Unassigned.
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map((e, idx) => (
              <TeamRow
                key={e.id}
                entry={e}
                rrId={rr.id}
                disabled={disabled}
                currentPool={poolNumber}
                poolChoices={poolChoices}
                seedNumber={idx + 1}
                onChanged={onChanged}
                onMoveUp={() => moveUp(idx)}
                onMoveDown={() => moveDown(idx)}
                isFirst={idx === 0}
                isLast={idx === sorted.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// Unassigned strip — same visual language as pool sections, but styled as
// a soft warning when it has contents, and a compact success badge when
// empty.
function UnassignedSection({ rr, entries, disabled, onChanged, poolChoices }) {
  const count = entries.length;

  if (count === 0) {
    return (
      <div
        data-testid="rrpb-unassigned-all-clear"
        className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm w-fit"
      >
        <CheckCircle size={16} weight="fill" className="text-emerald-700 shrink-0" />
        <span>All teams assigned to a pool</span>
      </div>
    );
  }

  return (
    <section
      className="border border-amber-200 rounded-2xl bg-amber-50/40 overflow-hidden"
      data-testid="rrpb-unassigned"
    >
      <header className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-amber-50 border-b border-amber-200">
        <span
          className="inline-flex items-center justify-center h-6 px-2 rounded-full bg-amber-500 text-white text-[11px] font-semibold shrink-0 tabular-nums"
        >
          !
        </span>
        <h3 className="text-base sm:text-lg font-semibold text-amber-900 flex-1 truncate">
          Unassigned
        </h3>
        <span
          className="text-[11px] uppercase tracking-wider text-amber-800 font-medium"
          data-testid="rrpb-unassigned-count"
        >
          {count} {count === 1 ? 'team' : 'teams'}
        </span>
      </header>
      <div className="p-3 sm:p-4 space-y-2">
        {entries.map((e) => (
          <TeamRow
            key={e.id}
            entry={e}
            rrId={rr.id}
            disabled={disabled}
            currentPool={null}
            poolChoices={poolChoices}
            seedNumber={null}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

// ================== root ==================

export default function RRPoolBuilder({ rr, onChanged }) {
  const disabled = POOL_LOCKED_STATUSES.has(rr.status);
  const numPools = rr.num_pools || 0;

  const poolChoices = useMemo(
    () => Array.from({ length: numPools }, (_, i) => ({
      value: i + 1,
      label: poolLabelFor(rr, i + 1),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numPools, rr.pool_labels],
  );

  const byPool = useMemo(() => {
    const map = {};
    for (let i = 1; i <= numPools; i++) map[i] = [];
    map.__unassigned = [];
    (rr.entries || []).forEach((e) => {
      if (e.pool_number != null && map[e.pool_number]) map[e.pool_number].push(e);
      else map.__unassigned.push(e);
    });
    return map;
  }, [rr.entries, numPools]);

  const unassignedCount = byPool.__unassigned.length;
  const totalTeams = (rr.entries || []).length;

  const createPool = async () => {
    try {
      await roundRobinAPI.createPool(rr.id);
      toast.success('Pool added.');
      onChanged?.();
    } catch (e) { reportError(e, 'Could not add pool.'); }
  };

  const autoAssign = async () => {
    if (!window.confirm('Auto-assign every team to a pool by snake seeding? This overwrites current assignments.')) return;
    try {
      await roundRobinAPI.assignPools(rr.id, []);
      toast.success('Pools auto-assigned.');
      onChanged?.();
    } catch (e) { reportError(e, 'Auto-assign failed.'); }
  };

  const reorderPool = async (poolNumber, direction) => {
    const target = poolNumber + direction;
    if (target < 1 || target > numPools) return;
    const order = [];
    for (let i = 1; i <= numPools; i++) order.push(i);
    const idxA = order.indexOf(poolNumber);
    const idxB = order.indexOf(target);
    [order[idxA], order[idxB]] = [order[idxB], order[idxA]];
    try {
      await roundRobinAPI.reorderPools(rr.id, order);
      onChanged?.();
    } catch (e) { reportError(e, 'Reorder failed.'); }
  };

  // Court Priority (migration 047) lives on the RR as pool_court_priority.
  // Editable except once the event is fully done — it only steers the next
  // auto-schedule run's court QUALITY, never timing or placed matches.
  const priorities = rr.pool_court_priority || {};
  const priorityEditable = !['completed', 'archived', 'cancelled'].includes(rr.status);
  const setPriority = async (poolNumber, value) => {
    const next = { ...(rr.pool_court_priority || {}) };
    if (value == null) delete next[String(poolNumber)];
    else next[String(poolNumber)] = value;
    try {
      await roundRobinAPI.update(rr.id, {
        pool_court_priority: Object.keys(next).length ? next : null,
      });
      toast.success('Court Priority saved.');
      onChanged?.();
    } catch (e) { reportError(e, 'Could not save Court Priority.'); }
  };

  return (
    <div
      className="rr-pool-builder-wide border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-4"
      data-testid="rr-pool-builder"
    >
      {/* Section header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">
            Pool Builder
          </div>
          <div className="text-[12px] text-[#6a766e] mt-1 max-w-2xl">
            {disabled
              ? 'Pool configuration is locked — the schedule has been generated or the event has started.'
              : 'Move teams between pools, reorder them for seeding, and rename pools. Pools may have different sizes. Schedule cannot be generated until every team is assigned.'}
          </div>
        </div>
        {!disabled && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={createPool}
              data-testid="rrpb-add-pool"
              className="px-3 py-1.5 rounded-full border border-[#013523] text-[#013523] text-xs font-medium flex items-center gap-1 hover:bg-[#f5f0e6]"
              type="button"
            >
              <Plus size={12} /> Add pool
            </button>
            <button
              onClick={autoAssign}
              data-testid="rrpb-auto-assign"
              className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-[#013523] text-xs font-medium flex items-center gap-1 hover:bg-[#fbfaf8]"
              type="button"
            >
              <ArrowsClockwise size={12} /> Auto-assign
            </button>
          </div>
        )}
      </div>

      {/* Global "some teams unassigned" warning banner — retained for
          continuity so the admin sees the exact "N of M" text on load. */}
      {unassignedCount > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2 text-[12.5px]"
          data-testid="rrpb-unassigned-warning"
          role="status"
        >
          {unassignedCount} of {totalTeams} team{totalTeams === 1 ? '' : 's'} not yet assigned to a pool. Schedule cannot be generated until every team has a pool.
        </div>
      )}

      {/* Unassigned area — compact when empty, full section when populated. */}
      <UnassignedSection
        rr={rr}
        entries={byPool.__unassigned}
        disabled={disabled}
        onChanged={onChanged}
        poolChoices={poolChoices}
      />

      {/* Pools stacked vertically, full width each. */}
      <div className="space-y-4" data-testid="rrpb-pools">
        {Array.from({ length: numPools }, (_, i) => i + 1).map((pn) => (
          <PoolSection
            key={pn}
            rr={rr}
            poolNumber={pn}
            entries={byPool[pn] || []}
            disabled={disabled}
            poolChoices={poolChoices}
            canDelete={(byPool[pn] || []).length === 0 && numPools > 1}
            canMoveUp={pn > 1}
            canMoveDown={pn < numPools}
            onReorderPool={(dir) => reorderPool(pn, dir)}
            priority={priorities[String(pn)] ?? ''}
            priorityEditable={priorityEditable}
            onPriorityChange={(v) => setPriority(pn, v)}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}
